import { BackendError, Service } from '@genial/services';
import { MetaFilterResponsePensionFiltersPensionOfferFilters } from '@genial/services/dist/models';
import { MetaResponse, PensionOfferDetailsSuccessResponse } from '@genial/services/src/models';
import { deburr } from 'lodash';
import { selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { metaFilterToOptions } from '../../../utils/metaFilterToOptions';
import { transformSortItem } from '../../../utils/transformSortItem';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import { privatePensionProductsLoadedState } from '../../product/atom';
import {
  normalizePrivatePensionOfferDetails,
  normalizePrivatePensionOffers,
} from '../../product/normalizer';
import {
  DynamicCustomFiltersProps,
  PrivatePensionDetailsNormalized,
  PrivatePensionEntityNormalized,
} from '../../product/types';
import { fixOrderFieldPrivatePension, screenName } from '../constants';
import { DynamicProductQueryParams } from '../types';

const mapProductCategory = (categories: string[]): string[] => {
  return categories
    .map((category) => deburr(category))
    .filter((value): value is string => value !== undefined);
};

export const privatePensionAvailableProductsQuery = selectorFamily<
  { data: PrivatePensionEntityNormalized[]; meta: MetaResponse },
  DynamicProductQueryParams
>({
  key: 'AvailableProducts/privatePensionAvailableProductsQuery',
  get: ({ filtersToApply, searchTerm, page, orderBy }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const sortItems = transformSortItem(orderBy);

        const sortItemsForApi =
          sortItems?.sort != null
            ? [
                {
                  sort: fixOrderFieldPrivatePension(sortItems.sort),
                  sortDirection: sortItems.sortDirection,
                },
              ]
            : undefined;

        const { data, meta } = await Service.Pension.V1.getOffersFilter({
          ...filtersToApply,
          ...(searchTerm ? { searchTerm } : {}),
          ...(sortItemsForApi ? { sortItems: sortItemsForApi } : {}),
          ...(filtersToApply.subCategories &&
            Array.isArray(filtersToApply.subCategories) && {
              productCategory: mapProductCategory(filtersToApply.subCategories),
            }),
          page,
          limit: 10,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = normalizePrivatePensionOffers(data);

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const privatePensionAvailableProductsFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  undefined
>({
  key: 'AvailableProducts/privatePensionAvailableProductsFiltersQuery',
  get: () => {
    return async ({ get }) => {
      const loadedPrivatePension = get(privatePensionProductsLoadedState) as {
        data: PrivatePensionEntityNormalized[];
        meta: MetaResponse;
        loaded: boolean;
      };

      if (!loadedPrivatePension.data.length) {
        return { filters: [] };
      }

      const meta = loadedPrivatePension.meta as MetaFilterResponsePensionFiltersPensionOfferFilters;
      const filter = meta?.filter;

      const categoryOptions = metaFilterToOptions(filter?.subCategories);

      return {
        filters: [
          {
            fieldName: 'subCategories',
            label: 'Subcategoria',
            options: categoryOptions,
          },
        ],
      };
    };
  },
});

export const privatePensionAvailableProductsDetailsQuery = selectorFamily<
  PrivatePensionDetailsNormalized | null | undefined,
  { productId?: number }
>({
  key: 'CustomerApplication/privatePensionDetailsQuery',
  get: ({ productId }) => {
    return async () => {
      if (!productId) {
        return;
      }

      const apiData = await Service.Pension.V1.getOffersDetails({
        productId,
        screenName,
      });

      const normalizedData = normalizePrivatePensionOfferDetails(
        apiData as PensionOfferDetailsSuccessResponse
      );

      return normalizedData;
    };
  },
});
